"use client"

import { useMemo } from 'react'
import { TIER_1_FIELDS, TIER_2_FIELDS, isMaskedValue } from '@/shared/lib/pii-utils'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'

export type PiiAccessLevel = 'full' | 'tier1' | 'none'

/**
 * Determine the current user's PII access level for a given data object.
 *
 * This works by inspecting what the *backend returned* — if Tier 1 fields
 * are present, the user has at least Tier 1 access. If Tier 2 fields are
 * present (even masked), the user has Tier 2 access.
 *
 * This is a heuristic: the backend is the source of truth for access control.
 * The frontend uses this to decide what UI to show (form fields vs. restricted badges).
 */
export function usePiiAccess(data: Record<string, unknown> | null | undefined): {
    accessLevel: PiiAccessLevel
    hasTier1: boolean
    hasTier2: boolean
    isSelf: boolean
    isAdmin: boolean
} {
    const { session } = useSession()
    const isAdmin = session?.role === UserRole.ADMIN
    const currentUserId = session?.sub

    return useMemo(() => {
        if (!data) {
            return { accessLevel: 'none' as const, hasTier1: false, hasTier2: false, isSelf: false, isAdmin }
        }

        // Check if the current user is viewing their own data
        const dataUserId = (data as any)?.id ?? (data as any)?.userId
        const isSelf = currentUserId != null && dataUserId != null && Number(currentUserId) === Number(dataUserId)

        // Self always has full access
        if (isSelf) {
            return { accessLevel: 'full' as const, hasTier1: true, hasTier2: true, isSelf: true, isAdmin }
        }

        // Check if any Tier 1 fields are present in the response
        const hasTier1 = [...TIER_1_FIELDS].some((field) => data[field] !== undefined)

        // Check if any Tier 2 fields are present (even masked)
        const hasTier2 = [...TIER_2_FIELDS].some(
            (field) => data[field] !== undefined && data[field] !== null,
        )

        let accessLevel: PiiAccessLevel = 'none'
        if (hasTier2) {
            accessLevel = 'full'
        } else if (hasTier1) {
            accessLevel = 'tier1'
        }

        return { accessLevel, hasTier1, hasTier2, isSelf, isAdmin }
    }, [data, currentUserId, isAdmin])
}

/**
 * Determine if a specific Tier 2 field has a masked value in the response data.
 * Used to conditionally show the "Reveal" button for users with tier2 access.
 */
export function isTier2Masked(data: Record<string, unknown> | null | undefined, fieldName: string): boolean {
    if (!data || !TIER_2_FIELDS.has(fieldName)) return false
    return isMaskedValue(data[fieldName])
}
